import type { Ref } from 'vue';
import type { FormType } from '#layers/core/app/runtime/utils/form';
import type { Pager } from '#layers/core/app/runtime/utils/pager';
import { ToolCallFailure } from '#layers/core/app/runtime/utils/agentToolsRegistry';

// Widok panelu dociąga dane w `onBeforeMount`, a narzędzia rejestruje w `onMounted` - a `await`
// z pierwszego haka rozwiązuje się PO drugim. Między jednym a drugim odczyt zwracał kształt
// początkowy, czyli same wartości puste, nieodróżnialny od pustego formularza. Agent, który policzył
// na tym nową wartość i zapisał, nadpisywał rekord danymi wziętymi z pustki - zdarzyło się to
// naprawdę, na rekordzie dostawy na środowisku deweloperskim
//
// Dlatego odczyt czeka na dane, zamiast oddać kształt początkowy jako treść rekordu. Czekanie stoi
// tutaj, a nie w widokach: układ obu haków jest ten sam na wszystkich podłączonych formularzach,
// więc poprawka w widoku musiałaby powtórzyć się tyle razy, ile jest podłączonych tras, i tyle samo
// razy dałoby się o niej zapomnieć

// Górna granica czekania. Czekanie bez granicy zawieszałoby najczęściej wołane narzędzie całego
// panelu na widoku, którego wczytywanie nigdy się nie kończy - a to byłoby gorsze od okna, które to
// czekanie zamyka. Po granicy idzie odmowa, nie cicha odpowiedź: te same puste wartości oddane po
// dziesięciu sekundach byłyby tym samym błędem, tylko późniejszym
export const AGENT_DATA_READY_TIMEOUT_MS = 10000;

// Ile czekamy na to, żeby wczytywanie w ogóle RUSZYŁO. Osobna, o rząd wielkości krótsza granica niż
// powyższa, bo odpowiada na inne pytanie. Widok nie zawsze zaczyna wczytywanie od razu: formularz
// dostawy najpierw czeka na własne przygotowanie (`await initData()` - pobiera listę krajów i sam
// wpisuje do formularza dni dostawy oraz ceny per kraj), a dopiero potem woła `ready`. W tym czasie
// formularz nie zgłasza ani "wczytane", ani "wczytuję" - i takiego stanu nie da się odróżnić od
// widoku, który nigdy niczego nie pobiera. Mierzone na żywym panelu: od rejestracji narzędzi do
// przyjścia encji dostawy mija około 460 ms, więc półtorej sekundy daje na to zapas
//
// Po upływie karencji odczyt ODPOWIADA, a nie odmawia: widok, który niczego nie pobiera (formularz
// ustawień, widok dodawania po przygotowaniu), ma wtedy w formularzu swoją prawdziwą treść
export const AGENT_DATA_READY_GRACE_MS = 1500;

// Do kiedy wolno czekać na to, żeby wczytywanie ruszyło. Liczone raz, przy rejestracji, i wyłącznie
// dla rejestracji wykonanej wewnątrz komponentu widoku - bo karencja pokrywa dokładnie pracę, którą
// widok wykonuje przy montowaniu. Poza komponentem żadnego montowania nie ma i nie ma na co czekać:
// rejestrację bez instancji wykonują testy narzędzi widoków, a kazanie im czekać półtorej sekundy na
// wczytanie, które nigdy nie nastąpi, opóźniałoby każdy z nich bez żadnej informacji w zamian
export function agentDataGraceUntil(inComponent: boolean): number
{
    return inComponent ? Date.now() + AGENT_DATA_READY_GRACE_MS : 0;
}

// Odstęp między pytaniami o stan. Na tyle krótki, żeby nie dokładać zauważalnego opóźnienia do
// odczytu, który i tak czeka na odpowiedź serwera, i na tyle długi, żeby przez te sekundy nie
// zajmować pętli zdarzeń panelu
const AGENT_DATA_READY_POLL_MS = 25;

// Trzy stany, nie jedna flaga - i to jest sedno tej naprawy. Pomocnik formularza ma dwie niezależne
// flagi, a ich trzecia kombinacja (ani wczytane, ani wczytywane) jest właśnie tym stanem, w którym
// widok dostawy stoi w chwili rejestracji narzędzi. Pytanie o samo "czy trwa wczytywanie" tego stanu
// nie widzi i przepuszcza kształt początkowy dalej - sprawdzone na uruchomionym panelu
export interface AgentDataReadyState
{
    // Dane doszły. Dla formularza znaczy to, że widok ogłosił zakończenie wczytywania, choćby
    // niepowodzeniem; dla listy - że przyszła prawdziwa liczba wierszy
    loaded: boolean;

    // Wczytywanie trwa w tej chwili
    loading: boolean;
}

// Pytanie w pętli, a nie obserwator reaktywności Vue. Stan, na który czekamy, leży w dwóch różnych
// konstrukcjach - pomocnik formularza i pager listy - a w widoku bywa dodatkowo schowany za
// `computed` nad store albo za `reactive` zamiast `ref`. Mechanizm nie ma jak sprawdzić, czy
// reaktywność sięga do każdej z nich, a obserwator, do którego nie sięga, po prostu milczy.
// Milczenie kończy się tu odmową po upływie granicy, czyli zamieniłoby odczyt działający dziś
// w odmowę - dokładnie tę regresję, której ten mechanizm ma nie wprowadzić. Pytanie w pętli czyta tę
// samą właściwość, z której powstaje odpowiedź, więc nie ma jak się z nią rozjechać
export async function awaitAgentDataReady(state: () => AgentDataReadyState, graceUntil: number, toolName: string, timeoutMs: number = AGENT_DATA_READY_TIMEOUT_MS): Promise<void>
{
    const deadline = Date.now() + timeoutMs;

    while (notReadyYet(state(), graceUntil))
    {
        if (Date.now() >= deadline)
        {
            throw new ToolCallFailure(
                'data-not-loaded',
                `Widok nie skończył wczytywać danych w ciągu ${Math.round(timeoutMs / 1000)} s, więc ${toolName} nie ma czego zwrócić. Wartości, które widok ma teraz, to kształt początkowy, a nie treść wczytywanego rekordu - oddanie ich wyglądałoby jak pusty formularz. Powtórz odczyt albo sprawdź, czy wczytywanie nie skończyło się błędem.`
            );
        }

        await new Promise(resolve => setTimeout(resolve, AGENT_DATA_READY_POLL_MS));
    }
}

// Czy jeszcze nie ma na czym oprzeć odpowiedzi. Sprawdzane przy każdym obrocie, bo widok przechodzi
// przez te stany po kolei
function notReadyYet(state: AgentDataReadyState, graceUntil: number): boolean
{
    // Dane są - i zostają. Po pierwszym wczytaniu ten warunek jest prawdziwy do końca życia widoku,
    // więc odczyt na 237 podłączonych trasach kosztuje tyle samo co przed tą zmianą
    if (state.loaded)
    {
        return false;
    }

    if (state.loading)
    {
        return true;
    }

    // Ani jedno, ani drugie: albo wczytywanie jeszcze nie ruszyło, albo ten widok niczego nie pobiera.
    // Rozstrzyga o tym karencja liczona od rejestracji - po niej przyjmujemy to drugie i odpowiadamy
    return Date.now() < graceUntil;
}

// Flagi pomocnika czytane wprost, a nie przez jego metody `loading()` i `loaded()`. Powód: `loaded()`
// pomocnika znaczy "nie trwa teraz wczytywanie", a nie "dane doszły" - dla formularza, który jeszcze
// nic nie pobrał, jest prawdą, czyli odpowiadałby na inne pytanie niż to zadane tutaj
//
// `$loaded` zapala `complete()`, czyli koniec `ready(...)` - również wtedy, gdy wczytywanie skończyło
// się niepowodzeniem, bo widoki łapią wyjątek serwisu i oddają `false`. To jest właściwe zachowanie:
// po nieudanym wczytaniu formularz ma swój kształt początkowy i to jest cała treść, jaką widok ma
//
// Przeładowanie po zapisie zgłaszane samym `wait()` nie cofa `$loaded`, więc odczyt w jego trakcie
// nie czeka. Jest to rozstrzygnięcie, nie przeoczenie - w trakcie takiego przeładowania formularz
// trzyma poprzednio wczytaną encję, a nie kształt początkowy, więc pomyłka "pustka wzięta za treść
// rekordu" nie ma tam jak powstać. Widok, który woła `wait(true)`, gasi `$loaded` i wraca pod to
// czekanie
//
// Zmiana rekordu nie idzie tą drogą. Inny `id` w adresie to nowy komponent z nowym formularzem
// (strona albo, pod stałym kluczem strony, formularz z `:key` po rekordzie), więc jego `$loaded`
// startuje zgaszone. Wpisy starego komponentu zdejmuje czyszczenie przy zmianie widoku w
// `plugins/tools.ts` - bez niego odczyt trafiałby w formularz poprzedniego rekordu
export function formReadyState(form: Ref<FormType<any>>): AgentDataReadyState
{
    const helper = form.value as { $loaded?: unknown; $loading?: unknown };

    return { loaded: helper.$loaded === true, loading: helper.$loading === true };
}

// Lista ma stan wczytywania w pagerze: widoki zapalają go w pierwszej instrukcji swojej funkcji
// czytającej dane i gaszą w `finally`, więc obejmuje również wczytywanie zakończone błędem.
// "Wczytana" jest po prawdziwej liczbie wierszy - pager powstaje z `Number.MAX_SAFE_INTEGER`
// i dopiero odpowiedź serwera podstawia tam liczbę, także zero po niepowodzeniu
//
// Lista bez pagera (drzewo działów) nie ma żadnego z tych dwóch sygnałów. Zgłaszamy ją jako wczytaną,
// żeby odczyt odpowiadał od razu - czekanie nie miałoby tam na czym stanąć, a zmyślony stan
// "wczytuję" kazałby jej czekać do końca karencji przy każdym wejściu na widok
export function listReadyState(pager: Ref<Pager> | undefined): AgentDataReadyState
{
    if (pager === undefined)
    {
        return { loaded: true, loading: false };
    }

    return { loaded: pager.value.getTotalRows() !== Number.MAX_SAFE_INTEGER, loading: pager.value.loading === true };
}
